import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Question } from '../../schemas/schemas';

interface QuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (question: Question) => void;
  editingQuestion: Question | null;
  translations?: {
    title?: string;
    editQuestion?: string;
    addQuestion?: string;
    questionText?: string;
    questionType?: string;
    answerOptions?: string;
    correctAnswer?: string;
    difficulty?: string;
    tags?: string;
    cancel?: string;
    save?: string;
    saveChanges?: string;
    addOption?: string;
    [key: string]: string | undefined;
  };
}

export default function QuestionModal({ isOpen, onClose, onSave, editingQuestion, translations = {} }: QuestionModalProps) {
  // Get the initial question type from the editing question or default to multiple choice
  const initialType = editingQuestion?.question_type || 'multiple_choice';
  const [questionType, setQuestionType] = useState<string>(initialType);
  
  // Get options for multiple choice questions 
  const initialOptions = (): string[] => {
    if (editingQuestion && editingQuestion.question_type === 'multiple_choice') {
      return editingQuestion.options || ['', ''];
    }
    return ['', ''];
  };
  
  const [options, setOptions] = useState<string[]>(initialOptions());
  
  // Early return if modal is not open
  if (!isOpen) return null;
  
  const isEditing = !!editingQuestion;
  
  // Handle form submission
  const handleSave = () => {
    // Create a new question or modify the existing one
    let newQuestion: Question;
    
    // Generate a random ID if not editing
    const questionId = editingQuestion?.question_id || `q${Date.now()}`;
    const text = document.getElementById('question-text') as HTMLTextAreaElement;
    
    // This is simplified - in a real app, we would use proper form handling
    if (questionType === 'multiple_choice') {
      // We know this will be a multiple choice question
      newQuestion = {
        question_id: questionId,
        question_text: text.value || 'Sample multiple choice question',
        question_type: 'multiple_choice',
        options: options.length ? options : ['Option A', 'Option B', 'Option C'],
        correct_option: options[0] || 'Option A', // Simplified - should come from selected radio
      };
    } else if (questionType === 'boolean') {
      // Get the selected boolean value
      const trueRadio = document.getElementById('true-answer') as HTMLInputElement;
      
      newQuestion = {
        question_id: questionId,
        question_text: text.value || 'Sample true/false question',
        question_type: 'boolean',
        correct_answer: trueRadio.checked ? 'true' : 'false',
      };
    } else {
      // Must be a text question
      const textAnswer = document.getElementById('text-answer') as HTMLInputElement;
      
      newQuestion = {
        question_id: questionId,
        question_text: text.value || 'Sample text input question',
        question_type: 'text',
        correct_answer: textAnswer.value || '',
      };
    }
    
    onSave(newQuestion);
    onClose();
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <CardHeader className="border-b border-gray-200 dark:border-gray-700">
          <CardTitle className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {isEditing ? (translations.editQuestion || 'Edit Question') : (translations.addQuestion || 'Add New Question')}
          </CardTitle>
          <CardDescription className="text-gray-600 dark:text-gray-400">
            {isEditing ? 'Modify the selected question' : 'Create a new question for this exercise'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label htmlFor="question-text" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {translations.questionText || 'Question Text'}
              </label>
              <textarea
                id="question-text"
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:text-sm"
                placeholder="Enter the question text"
                defaultValue={editingQuestion?.question_text || ''}
              ></textarea>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                {translations.questionType || 'Question Type'}
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setQuestionType('multiple_choice')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    questionType === 'multiple_choice'
                      ? 'bg-blue-100 text-blue-800 border-2 border-blue-300 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-600'
                      : 'bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Multiple Choice
                </button>
                
                <button
                  type="button"
                  onClick={() => setQuestionType('boolean')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    questionType === 'boolean'
                      ? 'bg-green-100 text-green-800 border-2 border-green-300 dark:bg-green-900/30 dark:text-green-200 dark:border-green-600'
                      : 'bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                  True/False
                </button>
                
                <button
                  type="button"
                  onClick={() => setQuestionType('text')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    questionType === 'text'
                      ? 'bg-purple-100 text-purple-800 border-2 border-purple-300 dark:bg-purple-900/30 dark:text-purple-200 dark:border-purple-600'
                      : 'bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Short Answer
                </button>
                
                <button
                  type="button"
                  onClick={() => setQuestionType('fill_in_blank')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    questionType === 'fill_in_blank'
                      ? 'bg-orange-100 text-orange-800 border-2 border-orange-300 dark:bg-orange-900/30 dark:text-orange-200 dark:border-orange-600'
                      : 'bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Fill in the Blank
                </button>
              </div>
            </div>
            
            {questionType === 'multiple_choice' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {translations.answerOptions || 'Answer Options'}
                </label>
                <div className="space-y-3">
                  {options.map((option, index) => (
                    <div key={index} className="flex items-center group">
                      <div className="flex items-center flex-1">
                        <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center mr-3 group-hover:border-blue-400 transition-colors">
                          <div className="w-2 h-2 rounded-full bg-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        </div>
                        <input
                          type="text"
                          className="flex-1 text-sm border-none outline-none bg-transparent placeholder-gray-400 focus:placeholder-gray-300"
                          placeholder={`Option ${index + 1}`}
                          defaultValue={option}
                          onChange={(e) => {
                            const newOptions = [...options];
                            newOptions[index] = e.target.value;
                            setOptions(newOptions);
                          }}
                        />
                      </div>
                      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          onClick={() => {
                            // Set as correct answer
                            // This is a simplified approach - in a real app you'd track correct answer separately
                            console.log('Set as correct answer:', option);
                          }}
                          title="Set as correct answer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          onClick={() => {
                            if (options.length > 2) {
                              setOptions(options.filter((_, i) => i !== index));
                            }
                          }}
                          title="Remove option"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  <button
                    type="button"
                    className="flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                    onClick={() => setOptions([...options, ''])}
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    {translations.addOption || 'Add option'}
                  </button>
                </div>
              </div>
            )}
            
            {questionType === 'boolean' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {translations.correctAnswer || 'Correct Answer'}
                </label>
                <div className="space-y-3">
                  <div className="flex items-center group">
                    <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center mr-3 group-hover:border-blue-400 transition-colors">
                      <div className="w-2 h-2 rounded-full bg-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    </div>
                    <span className="text-sm text-gray-700 dark:text-gray-300">True</span>
                  </div>
                  <div className="flex items-center group">
                    <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center mr-3 group-hover:border-blue-400 transition-colors">
                      <div className="w-2 h-2 rounded-full bg-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    </div>
                    <span className="text-sm text-gray-700 dark:text-gray-300">False</span>
                  </div>
                </div>
              </div>
            )}
            
            {(questionType === 'text' || questionType === 'fill_in_blank') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {translations.correctAnswer || 'Correct Answer'}
                </label>
                <div className="space-y-3">
                  <div className="flex items-center group">
                    <div className="w-6 h-6 rounded border-2 border-gray-300 flex items-center justify-center mr-3 group-hover:border-blue-400 transition-colors">
                      <div className="w-2 h-2 rounded bg-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    </div>
                    <input
                      type="text"
                      className="flex-1 text-sm border-none outline-none bg-transparent placeholder-gray-400 focus:placeholder-gray-300"
                      placeholder="Enter the correct answer"
                      defaultValue={editingQuestion?.correct_answer || ''}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex justify-end space-x-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <Button 
            variant="outline" 
            onClick={onClose}
            className="px-6 py-2 text-sm font-medium"
          >
            {translations.cancel || 'Cancel'}
          </Button>
          <Button 
            onClick={handleSave}
            className="px-6 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isEditing ? (translations.saveChanges || 'Save Changes') : (translations.save || 'Save')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
} 